import React from 'react';
import { ButtonV2, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useEmailStepController } from './EmailStep.controller';
import { useTranslation } from '@shared/core';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display4ToHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { StyledContent, StyledHeaderStack, StyledTitleStack, StyledFormStack, StyledInputsStack, styles } from '../../UnlockDialog/UnlockForm/UnlockForm.styles';
import { useDeferredInputFocus } from '../useDeferredInputFocus';

type EmailStepProps = Readonly<{
  eventId: string;
  onCodeSent: (email: string) => void;
  // Optional — when omitted (e.g. in a hard full-site gate), the "Continue as
  // guest" skip button is not rendered.
  onSkip?: () => void;
  // Whether the step is currently visible. The dialog passes `isOpen` here so
  // focus is deferred until the open animation completes; the gate omits it
  // (the gate is always visible when mounted, default `true`).
  isActive?: boolean;
}>;

export const EmailStep: React.FC<EmailStepProps> = ({ eventId, onCodeSent, onSkip, isActive = true }) => {
  const { formik, loading } = useEmailStepController({ eventId, onCodeSent });
  const { tq } = useTranslation('guestUnlock');
  const inputRef = useDeferredInputFocus<HTMLInputElement>(isActive);

  return (
    <StyledContent __css={styles.content}>
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={display4ToHeadingOverride}>
            <TextV2 typographyVariant="hed5">{tq`Let us find your invitation`}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2
              typographyVariant="body2"
              color="mono10"
            >
              {tq`Enter the email address associated with your invitation.`}
            </TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <FormControl label={tq`Email`}>
              <InputV2
                ref={inputRef}
                placeholder={tq`e.g. sarahsmith@example.com`}
                isInvalid={(!!formik.errors.email && formik.touched.email) || undefined}
                isDisabled={loading}
                type="email"
                autoComplete="email"
                {...formik.getFieldProps('email')}
              />
            </FormControl>
          </StyledInputsStack>
          <ButtonV2
            shape="rounded"
            width="100%"
            intent="neutral"
            type="submit"
            loading={loading}
          >
            {tq`Verify`}
          </ButtonV2>
          {onSkip && (
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              width="100%"
              marginTop={4}
              onClick={onSkip}
            >
              {tq`Continue as guest`}
            </ButtonV2>
          )}
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
